import '../styles/segmentedControl.less';
import $ from 'jquery';
import * as common from '../../../shared/modules/common';
import '../../../shared/modules/jquery.truncate';
import { init as initTooltip } from '../../Tooltip/js/tooltip';

/**
 * Get the topmost invisible parent of an element.
 * @param $element The invisible element.
 * @return The topmost invisible element which descendant is the parameter.
 */
function findTopmostInvisibleParent($element) {
    var $current = $element,
        $nonVisible = $element;

    while (!$current.is(':visible')) {
        $nonVisible = $current;
        $current = $current.parent();
    }

    return $nonVisible;
}

/**
 * Make the first visible parent of an segmented control visible.
 * @param $control The segmented controlled.
 * @return A JSON which contains the following properties:
 * - $firstNonVisibleParentElement The element which was the first non visible parent.
 *   It is a Jquery object.
 * - originalDisplay The original value for display in the style attr if it was set.
 * - originalPosition The original value for position in the style attr if it was set.
 * - originalVisibility The original value for visibility in the style attr if it was set.
 * - wasVisible The segmented control is visible or not when the function is called.
 *   If it is visible then the other parameters are not set.
 */
function makeTopmostInvisibleParentVisible($control) {
    var isVisible = $control.is(':visible'),
        $nonVisible,
        cssAttrValue,
        cssTokens,
        cssTrimTokens = {
            'display': '',
            'position': '',
            'visibility': ''
        };

    if (!isVisible) {
        $nonVisible = findTopmostInvisibleParent($control);

        cssAttrValue = $nonVisible.attr('style');

        if (cssAttrValue) {
            cssTokens = cssAttrValue.split(';');
            $.each(cssTokens, function (index, el) {
                var parts = el.split(':');
                cssTrimTokens[$.trim(parts[0])] = parts[1];
            });
        }

        $nonVisible.css('display', 'block');
        $nonVisible.css('visibility', 'hidden');
        $nonVisible.css('position', 'absolute');
    }

    return {
        $firstNonVisibleParentElement: $nonVisible,
        originalDisplay: cssTrimTokens.display,
        originalPosition: cssTrimTokens.position,
        originalVisibility: cssTrimTokens.visibility,
        wasVisible: isVisible
    };
}

/**
 * Revert the non visible parent to its original state.
 */
function revertInvisibleParent(parentInfo) {
    if (!parentInfo.wasVisible) {
        parentInfo.$firstNonVisibleParentElement.css({
            'display': parentInfo.originalDisplay,
            'position': parentInfo.originalPosition,
            'visibility': parentInfo.originalVisibility
        });
    }
}

/**
 * Run dimension related function on an element, which could be invisible.
 */
function runDimensionFunctionOn($element, dimensionFunction) {
    var parentInfo = makeTopmostInvisibleParentVisible($element),
        result = dimensionFunction.call(this, $element);
    revertInvisibleParent(parentInfo);
    return result;
}

function calculateMaxControlItemHeight($control) {
    var height = 0;
    $control.find('.uwr-segmented-control-button').each(function () {
        var currentControlItemHeight = $(this).height();
        if (currentControlItemHeight > height) {
            height = currentControlItemHeight;
        }
    });
    return height;
}

function isFixedVerticalLayout($control) {
    var layoutAttr = $control.attr('data-layout');
    return layoutAttr === 'vertical' || layoutAttr === 'v' || layoutAttr === 'V';
}

function isFixedHorizontalLayout($control) {
    var layoutAttr = $control.attr('data-layout');
    return layoutAttr === 'horizontal' || layoutAttr === 'h' || layoutAttr === 'H';
}

function isDynamicLayout($control) {
    return !isFixedVerticalLayout($control) && !isFixedHorizontalLayout($control);
}

function resize($control) {
    var $controlItems = $control.find('.uwr-segmented-control-button'),
        fullControlWidth = $control.attr('data-width'),
        controlItemWidth = fullControlWidth / $controlItems.length,
        controlItemHeight = $control.height(),
        orientation = 'h';

    function setOrientationClass($element, orientation) {
        if (orientation === 'h') {
            $element.addClass('uwr-segmented-control-horizontal');
            $element.removeClass('uwr-segmented-control-vertical');
        } else {
            $element.removeClass('uwr-segmented-control-horizontal');
            $element.addClass('uwr-segmented-control-vertical');
        }
    }
    function updateButton(index, element) {
        var $controlItem = $(element),
            $contents = $controlItem.clone().contents(),
            btnText = $controlItem.text(),
            btnHtml = $controlItem.html(),
            btnDisabled = $controlItem.attr('data-disabled');
        if (btnDisabled === 'true') {
            $controlItem.addClass('uwr-segmented-control-disabled');
        }
        if (orientation === 'h') {
            $controlItem.height = controlItemHeight;
        }
        if ($controlItem.get(0).tagName === 'BUTTON') {
            $controlItem.width((orientation === 'h') ? controlItemWidth - 1 : '100%');
        }
        $controlItem.truncate({
            visibleTrailChars: 0,
            width: $controlItem.width() * 2 - 20,
            token: '\u2026'
        });
        if ($controlItem.text() !== btnText) {
            $controlItem.addClass('uwr-tooltip');
            $controlItem.attr('data-trigger', 'focus,hover');
            $controlItem.attr('data-text', btnHtml);
            $controlItem.attr('data-type', 'neutral');
            $controlItem.attr('data-placement', 'top');
            $contents.each(function () {
                if (this.length === undefined) {
                    $controlItem.append($(this));
                }
            });
        }
    }

    $control.width(fullControlWidth);

    if ((fullControlWidth > $control.parent().width() && !isFixedHorizontalLayout($control))
            || isFixedVerticalLayout($control)) {
        fullControlWidth = controlItemWidth;
        orientation = 'v';
    }

    $control.width(fullControlWidth);
    setOrientationClass($control, orientation);
    $controlItems.each(updateButton);
    controlItemHeight = calculateMaxControlItemHeight($control);
    $controlItems.each(function () {
        $(this).height(controlItemHeight);
    });
}

function getTopPadding($outerSpan, $innerSpan) {
    return runDimensionFunctionOn($outerSpan, function () {
        return Math.floor(($outerSpan.height() - $innerSpan.height()) / 2); // padding
    });
}

function setVerticalAlignOfControlContent($outerSpan) {
    var $innerSpan = $('<span></span>'),
        $tooltipSpan = $outerSpan.find('.uwr-tooltip'),
        topPadding;
    if ($tooltipSpan.length > 0) {
        $innerSpan = $tooltipSpan;
    } else {
        $innerSpan.append($outerSpan.contents());
        $outerSpan.empty();
        $outerSpan.append($innerSpan);
    }

    topPadding = getTopPadding($outerSpan, $innerSpan);
    $innerSpan.css('top', topPadding);
}

function resizeAll() {
    $(window.document).find('.uwr-segmented-control').each(function () {
        runDimensionFunctionOn($(this), resize);
    });

    $(window).resize(function () {
        $('.uwr-segmented-control').each(function () {
            var $this = $(this);
            if ($this.attr('data-auto-collapse') !== 'false' && isDynamicLayout($this)) {
                resize($this);
            }
        });
    });
}

function toggleItemSelection(selector) {
    var $controlItem = $(selector),
    $parent = $controlItem.closest('.uwr-segmented-control'),
    $prevSelected = $parent.find('.uwr-segmented-control-selected');

    $prevSelected.removeClass('uwr-segmented-control-selected');
    $controlItem.toggleClass('uwr-segmented-control-selected');

    var prevValue = $controlItem.find('.uwr-segmented-control-hidden-accessible').prop('checked');
    $controlItem.find('.uwr-segmented-control-hidden-accessible').prop('checked', !prevValue);

    setClasses($parent);
}

function setClickHandlers() {
    $('.uwr-segmented-control').each(function () {
        var $control = $(this),
            initialized = $control.data('initialized');
        if (!initialized) {
            $control.data('initialized', true);

            $control.find('.uwr-segmented-control-button').each(function () {
                var $controlItem = $(this),
                    $input = $controlItem.find('input'),
                    clickEventHandler = $controlItem.prop('onclick');
                if ($input.length > 0) {
                    clickEventHandler = $input.prop('onclick');
                    $input.prop('onclick', null);
                } else {
                    $controlItem.prop('onclick', null);
                }
                $controlItem.click(function (event) {
                    if (!$controlItem.hasClass('uwr-segmented-control-disabled')
                            && (!$controlItem.hasClass('uwr-segmented-control-selected')
                            || $input.length && $input.attr('type').toLowerCase() === 'checkbox')) {
                        toggleItemSelection($controlItem);
                        if (clickEventHandler) {
                            clickEventHandler.call(this, event);
                        }
                    }
                });
                setVerticalAlignOfControlContent($controlItem);
            });
        }
        $control.css('visibility', 'inherit');
    });
}

function copyDataAttributes($target, $source) {
    var regexp = /^data\-(.+)$/;
    $.each($source[0].attributes, function (index, attr) {
        if (regexp.test(attr.nodeName)) {
            var key = attr.nodeName.match(regexp)[1];
            $target.attr('data-' + key, attr.nodeValue);
        }
    });
}

function wrapInputs() {
    $('.uwr-segmented-control-button').each(function () {
        var $controlItem = $(this),
            controlItem = $controlItem.get(0);
        if (controlItem.nodeName === 'INPUT') {
            var $outerSpan = $('<span></span>'),
                classes = $controlItem.attr('class'),
                next = controlItem.nextSibling;

            // Set up classes
            if ($controlItem.attr('checked')) {
                classes += ' uwr-segmented-control-selected';
            }
            $outerSpan.attr('class', classes);
            $controlItem.removeAttr('class');
            $controlItem.addClass('uwr-segmented-control-hidden-accessible');

            // Build structure
            if (next && next.nodeValue) {
                $outerSpan.append($(next));
            }
            copyDataAttributes($outerSpan, $controlItem);
            $controlItem.wrap($outerSpan);
        }
    });
}

function setClasses($parent) {
    $parent.find('.uwr-segmented-control-hidden-accessible').each(function () {
        var $this = $(this),
            $controlItem = $this.closest('.uwr-segmented-control-button');
        if ($this.prop('checked')) {
            $controlItem.addClass('uwr-segmented-control-selected');
        } else {
            $controlItem.removeClass('uwr-segmented-control-selected');
        }
    });
}

/**
 * Initializes all segmented controls on the page. Should be called by client apps only after injecting
 * segmented controls dynamically.
 *
 * init() is an asynchronous call, so if some logic needs to be added once init() is finished, a callback
 * function can be passed in as a parameter.
 *
 * @param callback A Javascript function object that will be called at the end of the init() call.
 */
export function init(callback) {
    wrapInputs();
    resizeAll();
    initTooltip();
    setClickHandlers();

    common.invoke(callback);
}

/**
 * Selects a particular button of a Segmented Control.
 *
 * @param selector CSS selector string specifying the button's element (span, button or input).
 * @param callback Function object that is executed when the given button has been toggled.
 */
export function toggle(selector, callback) {
    toggleItemSelection(selector);
    common.invoke(callback);
}



//////////////////
// WEBPACK FOOTER
// ./widgets/SegmentedControl/js/segmentedControl.js
// module id = 38
// module chunks = main